// What the address bar should say, as a function of what is on screen.
//
// The URL rules here are subtle, so they are kept as pure functions of state
// and tested — the neutrality rules are exactly the kind of thing that
// silently regresses:
//
//   - A canonical URL (/, /{country}/, /{country}/{city}/) loads date- and
//     field-NEUTRAL and stays that way until the reader acts. Today's highlight
//     is a client-side enhancement, not a URL fact. `dateExplicit` / `modeExplicit`
//     gate those slugs — set by a user action or by a URL that already carried
//     the slug, cleared by the neutral startup seed. Never persisted: a reload of
//     a bare URL must come back bare.
//   - The map view addresses either a country (`countryFocus`: framed by a
//     /{country}/ route and not yet moved from) or an exact camera
//     (`cameraTouched`: the reader committed a view worth naming). Neither ⇒ the
//     untouched default map is just "/".
//
// The vocabulary itself — which slug means which date, which mode — lives in
// slug.ts. This module decides only which of them belong in the bar.

import { routePath, dayPath, comparePath } from './slug.ts';
import { dateSlugForKey, modeSlugForVar, countrySlug, citySlug } from './slug.ts';
import { VAR } from './variables.ts';
import type { ParsedRoute } from './slug.ts';
import type { VarKey } from './variables.ts';

export interface RouteFlags {
  dateExplicit: boolean;
  modeExplicit: boolean;
  cameraTouched: boolean;
  countryFocus: string | null;
}

export const NEUTRAL_FLAGS: RouteFlags = {
  dateExplicit: false,
  modeExplicit: false,
  cameraTouched: false,
  countryFocus: null,
};

// Only what the URL is a function of — deliberately not the whole app state, so
// a test can state a screen in six lines.
// A "Find your day" rating, as its address: the day, not the camera. `edited`
// means the recipe has been changed since the day produced it, so the day alone
// no longer reconstructs it and the URL has to carry ?r= as well.
export interface DayRouteInput {
  place: string;
  dateKey: string;
  bucket: string;
  edited: boolean;
}

export interface CompareRouteInput {
  places: string[];
  full: boolean;
}

export interface RouteInputs {
  view: 'landing' | 'map' | 'city' | 'compare';
  // The open city, as much of it as whatever opened it knew.
  city: { country?: string; citySlug?: string; nameAscii?: string; name?: string } | null;
  // The section key showing, e.g. "07-2". Undefined before the manifest lands.
  dateKey: string | undefined;
  variable: VarKey;
  recipeCanon: string;
  // The live camera, read by the caller only when it might be needed.
  camera: { lat: number; lng: number; zoom: number } | null;
  flags: RouteFlags;
  // Present only while the map is showing a rating authored from a day.
  day?: DayRouteInput | null;
  // Present only in the compare view.
  compare?: CompareRouteInput | null;
}

// Round the camera to what is worth putting in a URL: ~11 m of position, and a
// tenth of a zoom level.
export function roundCamera(lat: number, lng: number, zoom: number): { lat: number; lng: number; zoom: number } {
  const r4 = (n: number) => Math.round(n * 1e4) / 1e4;
  return { lat: r4(lat), lng: r4(lng), zoom: Math.round(zoom * 10) / 10 };
}

// The live state as URL parts. Landing returns {} → "/".
export function routePartsFor(i: RouteInputs): Partial<ParsedRoute> {
  // The date/field are in the URL only once the reader has chosen them (or the
  // URL arrived carrying them). Until then the canonical page stays neutral.
  const dateSlug = i.flags.dateExplicit && i.dateKey ? dateSlugForKey(i.dateKey) : null;
  const modeSlug = i.flags.modeExplicit ? modeSlugForVar(i.variable) : null;

  if (i.view === 'city' && i.city) {
    return {
      countrySlug: countrySlug(i.city.country),
      // The server is the authority on the city slug — it alone knows whether a
      // US name is ambiguous and needs its state (springfield-illinois). Every
      // payload that opens a city carries it; the plain slug is only a fallback
      // for a hint that somehow lacks it (an unambiguous city, so it is right).
      citySlug: i.city.citySlug || citySlug(i.city.nameAscii || i.city.name),
      dateSlug,
      modeSlug,
    };
  }

  if (i.view === 'map') {
    // A /{country}/ route framed the map and the reader has not moved it — keep
    // the country in the bar, not a coordinate.
    if (i.flags.countryFocus) return { countrySlug: i.flags.countryFocus, dateSlug, modeSlug };
    // The exact camera, so a shared/restored map link lands where the reader was.
    if (i.flags.cameraTouched && i.camera) return { coord: i.camera, dateSlug, modeSlug };
    // Untouched default camera → the bare landing/map path "/".
    return { dateSlug, modeSlug };
  }

  return {};
}

// The full path, including the recipe query. While "My kind of day" is the active
// field the URL carries the recipe too — that is what makes a custom map
// shareable: the path names the mode, ?r= names the recipe, and a fresh tab
// reconstructs both.
export function routePathFor(i: RouteInputs): string {
  const custom = !!(VAR[i.variable] && VAR[i.variable].custom);
  if (i.view === 'compare' && i.compare && i.compare.places.length) {
    const p = routePartsFor({ ...i, view: 'map', flags: { ...i.flags, cameraTouched: false, countryFocus: null } });
    return comparePath({ places: i.compare.places, dateSlug: p.dateSlug, modeSlug: p.modeSlug, full: i.compare.full })
      + (custom ? `?r=${i.recipeCanon}` : '');
  }
  // The day is the address of a rating authored from one — the date on the
  // ruler and the camera are where the reader is looking, not what they made.
  if (i.view === 'map' && custom && i.day) {
    return dayPath({ place: i.day.place, dateKey: i.day.dateKey, bucket: i.day.bucket }) + (i.day.edited ? `?r=${i.recipeCanon}` : '');
  }
  const search = custom ? `?r=${i.recipeCanon}` : '';
  return routePath(routePartsFor(i)) + search;
}

// A stable id for the current screen. A change of screen (city↔map↔landing, or a
// different city) is a history push so Back returns to it; an in-screen tweak
// (date/field/pan) just replaces, so scrubbing never floods history.
export function routeKeyFor(i: RouteInputs, cityId: number | null): string {
  if (i.view === 'city' && cityId != null) return `city:${cityId}`;
  if (i.view === 'compare' && i.compare) return `compare:${i.compare.places.join(',')}`;
  if (i.view === 'map' && i.day && VAR[i.variable] && VAR[i.variable].custom) return `day:${i.day.place}`;
  // A country-framed map is its own screen: panning away from /{country}/ is a
  // history push, so Back returns to the country page rather than replacing it.
  if (i.view === 'map') return i.flags.countryFocus ? `map:${i.flags.countryFocus}` : 'map';
  return 'landing';
}

// The flags an arriving URL implies. A slug present means "explicit", absent means
// "neutral" — deriving this on every read (not just on user action) is what makes
// Back/Forward symmetric: stepping from /canada/toronto/mid-may/ back to
// /canada/toronto/ drops the date slug again. Camera addressing resets too, so
// each restore is derived cleanly rather than inherited.
export function flagsForIncomingRoute(parts: Partial<ParsedRoute>): RouteFlags {
  return {
    dateExplicit: !!parts.dateSlug,
    modeExplicit: !!parts.modeSlug,
    cameraTouched: false,
    countryFocus: null,
  };
}

// Should this write push a new history entry, or replace the current one?
// `lastKey` is null before the first write of a screen (and after a route apply,
// which re-seeds it) — in that case there is nothing to have moved away from.
export function isHistoryPush(lastKey: string | null, key: string): boolean {
  return lastKey !== null && key !== lastKey;
}
